/**
 * The invite sheet's `Pools` tab body (ISSUE-197, R1).
 *
 * Two steps in one tab. First a list of the pools the caller may invite out
 * of, filtered by `resolveInviteSourcePoolsState`. Picking one hands over to
 * `InviteSourcePreview`, shared with the `Chats` tab, which runs
 * `/invite/bulk-preview` for that pool and owns the failed, empty and confirm
 * states. The source list here separates its own failure from its own empty
 * in the same way.
 *
 * When the fan runs no other pool, the empty state says why nothing is
 * listed: it counts the other pools they are in but do not run (ISSUE-1097).
 */
import { usePoolListMine } from "@sportsmet/pool-sdk/private";
import { Button, InlineBanner, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import {
  INVITE_BULK_RETRY_LABEL,
  POOLS_EMPTY_BODY,
  POOLS_EMPTY_TITLE,
  POOLS_FAILED_MESSAGE,
  POOLS_LOADING_LABEL,
  POOLS_SOURCE_HEADING,
  PREVIEW_EMPTY_BODY,
  PREVIEW_FAILED_MESSAGE,
  PREVIEW_LOADING_LABEL,
} from "./inviteSheetCopy";
import {
  poolsEmptyReason,
  resolveInviteSourcePoolsState,
} from "./inviteSourcePools";
import { InviteSourcePreview } from "./InviteSourcePreview";

export type typeInvitePoolsTabProps = {
  poolId: string;
};

type typeSource = { sourceId: string; sourceName: string };

export const InvitePoolsTab = ({
  poolId,
}: typeInvitePoolsTabProps): ReactNode => {
  const [source, setSource] = useState<typeSource | null>(null);

  const pools = usePoolListMine({});
  const listState = resolveInviteSourcePoolsState({
    isLoading: pools.isLoading,
    hasFailed: pools.isError || Boolean(pools.data?.error),
    errorCode: pools.data?.error?.code ?? null,
    items: pools.data?.data?.items ?? null,
    targetPoolId: poolId,
  });

  if (source === null) {
    return (
      <div className="flex flex-col gap-3" data-testid="invite-pools-tab">
        {listState.phase === "loading" ? (
          <div data-testid="invite-pools-loading">
            <Skeleton shape="control" label={POOLS_LOADING_LABEL} />
          </div>
        ) : null}
        {listState.phase === "failed" ? (
          <div data-testid="invite-pools-failed">
            <InlineBanner
              variant="warning"
              message={POOLS_FAILED_MESSAGE}
              action={{
                label: INVITE_BULK_RETRY_LABEL,
                onAction: () => {
                  void pools.refetch();
                },
              }}
            />
          </div>
        ) : null}
        {listState.phase === "empty" ? (
          <div className="flex flex-col gap-1" data-testid="invite-pools-empty">
            <p
              className="text-body text-ink"
              data-testid="invite-pools-empty-title"
            >
              {poolsEmptyReason({ otherPoolCount: listState.otherPoolCount }) ??
                POOLS_EMPTY_TITLE}
            </p>
            <p className="text-caption text-ink-secondary">
              {POOLS_EMPTY_BODY}
            </p>
          </div>
        ) : null}
        {listState.phase === "ready" ? (
          <>
            <h3 className="text-body-strong text-ink">
              {POOLS_SOURCE_HEADING}
            </h3>
            <ul className="flex flex-col gap-2">
              {listState.rows.map((row) => (
                <li key={row.poolId}>
                  <Button
                    fullWidth
                    variant="secondary"
                    title={row.name}
                    data-testid={`invite-source-pool-${row.poolId}`}
                    onClick={() => {
                      setSource({
                        sourceId: row.poolId,
                        sourceName: row.name,
                      });
                    }}
                  >
                    {/* Truncates like the Chats tab rows: a pool name runs to
                        80 characters and the kit Button never wraps. */}
                    <span className="min-w-0 truncate">{row.name}</span>
                  </Button>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="invite-pools-tab">
      <InviteSourcePreview
        poolId={poolId}
        source="pool"
        sourceId={source.sourceId}
        sourceName={source.sourceName}
        copy={{
          loadingLabel: PREVIEW_LOADING_LABEL,
          failedMessage: PREVIEW_FAILED_MESSAGE,
          emptyBody: PREVIEW_EMPTY_BODY,
        }}
        onBack={() => {
          setSource(null);
        }}
      />
    </div>
  );
};
